// FEAT-033: PC Seat Activation Panel
// 딜러가 고객 PC에서 RAYTeams 로컬 상태를 감지하고 PC 라이선스를 활성화/비활성화.
//
// UI 상태 흐름:
//  1. RAYTeams 미설치 → 안내 메시지
//  2. RAYTeams 미로그인(rayteamsUser === null) → 로그인 안내 + 진행 중지
//  3. 매칭 라이선스 없음(matchedVoucher === null) → 안내 메시지
//  4. 비활성 상태(N0008) + 활성 seat < 2 → [활성화] 버튼
//  5. 활성 상태(check: true) → [비활성화] 버튼
//  6. 동시 seat 2/2 → PC 목록 표시 → 선택 → [비활성화 후 활성화] 버튼
//  7. unique PC 5대 초과 → 안내 메시지
//
// IMP-151 AC-5: 비표현 로직은 usePcActivation 훅으로 이동. 본 파일은 JSX(표현) 만 담당.

import { Translate as t } from '../../../config/Translate';
import { interpolate } from '../../../config/i18nInterpolate';
// IMP-223: MAX_CONCURRENT_SEATS 는 interpolate(seatCount) 에서 사용 → 직접 import 유지.
import { MAX_CONCURRENT_SEATS } from '../../../lib/pcActivationStateResolver';
// IMP-438: 날짜 포맷 SSoT — 앱 언어 locale 로 렌더.
import { formatLicenseDate } from '../../../lib/licenseHelper';
import { ModalConfirm } from '../../../components/common/ModalConfirm';
import { usePcActivation } from './usePcActivation';

// IMP-384: {{key}} 인터폴레이션은 config/i18nInterpolate 의 공용 interpolate 로 통일(로컬 사본 제거).

const PCActivationPanel = ({
    dealerCustomerLicenses,
    // 사용자 요청 (2026-05-14): occupiedByOther alert 에 dealer 회사명 표시 위해
    // 부모 (DealerIssuePanel) 에서 companyMap 전달.
    companyMap = {},
    // FEAT-059: 제품(optional). 생략 = SMARTDent. 훅이 PC 활성화 지원 여부를 판정한다.
    product,
}) => {
    const {
        // state machine (resolver)
        uiState,
        action,
        activePcs,
        activeCount,
        uniqueLimitCount,
        isExternalScope,
        hasOtherLicenseInstalled,
        isLicenseStatusPending,
        isActivationUiVisible,
        // 파생 표현값
        infoBadge,
        otherLicenseInfo,
        accountName,
        accountEmail,
        showEmailSeparately,
        // raw bits
        systemSpecCheck,
        specCheckDismissed,
        message,
        loading,
        pcActivationLoading,
        selectedPcRtid,
        occupiedByOtherDismissed,
        // setter / 핸들러
        setSelectedPcRtid,
        dismissMessage,
        dismissSpecCheck,
        dismissOccupiedByOther,
        handleActivate,
        handleDeactivate,
        handleDeactivateOtherAndActivate,
        pendingConfirm,
        confirmPending,
        cancelPending,
    } = usePcActivation({ dealerCustomerLicenses, companyMap, product });

    // IMP-174 (사용자 요청 2026-05-11): 모든 early-return alert + loading 분기 하단 25px 마진.
    //   IMP-164 에서 rayteamsNotLoggedIn 만 적용했던 패턴을 전체로 확장.
    //   인접 Issue History 필터 영역과 시각 분리 — DealerIssuePanel 안에서 패널 다음에 다른 섹션이 따라옴.

    // ─── early-return uiState (IMP-223: resolver SSoT) ──────────────────────────
    // 상태 1: RAYTeams 미설치
    if (uiState === 'NOT_INSTALLED') {
        return (
            <div className="alert alert-warning py-2 small mb-0">
                {t('pcActivation.rayteamsNotInstalled')}
            </div>
        );
    }

    // 상태 2: RAYTeams 미로그인
    if (uiState === 'NOT_LOGGED_IN') {
        return (
            <div className="alert alert-warning py-2 small mb-0">
                {t('pcActivation.rayteamsNotLoggedIn')}
            </div>
        );
    }

    // 로딩 중(미확인 상태)
    if (uiState === 'LOADING') {
        return (
            <div className="text-muted small">
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                {t('common.status.loading')}
            </div>
        );
    }

    // 상태 3(매칭 라이선스 없음)은 early-return 하지 않고 카드 내부 메시지로 표시(패널 구조 일관성).

    // 상태 7: unique PC 5대 초과
    if (uiState === 'MAX_UNIQUE_REACHED') {
        return (
            <div className="alert alert-danger py-2 small mb-0">
                {t('pcActivation.maxUniqueReached')}
            </div>
        );
    }

    // BUG-113: ?purpose=check 응답의 code 별 정보성 안내 배지.
    // Confluence Local License Check API doc (2045050896) SSoT. PC seat 기반 분기와 병행.
    // 2026-05-14: noMatchedLicense + license-code-badge 두 분기를 resolveInfoBadge 로 공용화.

    // BUG-113 (사용자 추가 요청 2026-05-13): N0003 (No registered licenses) 시
    // 바우처 등록은 RAYGuard 책임 외 (별도 시스템). 따라서:
    //   - seat / activations 카운트 미표시 (PC 좌석 정보 무의미)
    //   - 활성화 / 비활성화 버튼 미표시 (활성화 동작 제공 안 함)
    // 단 로그인 정보 (사용자 이름 · email) 와 안내 배지는 유지 (어떤 팀즈 계정에 등록
    // 필요한지 식별).
    // isVoucherUnregistered(N0003) / isLicenseStatusPending / isActivationCodeBlocked(N0009/11/12)
    // / isActivationUiVisible 는 위 resolver 결과에서 destructure 됨 (IMP-223). 네 가드는 직교 —
    // voucher 매칭 / check 응답 도착 / N0003 자격 / 차단 코드(BE SSoT defense-in-depth).

    // BUG-113 (사용자 요청 2026-05-13): 법인 탭 / 딜러 탭 표시 통일.
    // raylink.json (rayteamsUser) 가 RAYTeams 계정의 SSoT — 항상 그 정보를 우선 사용.
    // PR #240 review (2026-05-14): name 영역과 email 영역 중복 출력 차단 + 헬퍼 외부화.

    // IMP-388 FR-012: 확인 모달 문구는 기존 키만 재사용. 확인 버튼만 동작 실행, 닫기/배경/ESC 는 취소.
    const CONFIRM_KEYS = {
        activate: ['pcActivation.activateBtn', 'pcActivation.activatePcConfirm'],
        deactivate: ['pcActivation.deactivateBtn', 'pcActivation.deactivatePcConfirm'],
        deactivateAndActivate: ['pcActivation.deactivateAndActivate', 'pcActivation.deactivateAndActivateConfirm'],
    };
    const confirmKeys = CONFIRM_KEYS[pendingConfirm];
    const confirmInfo = {
        showModal: !!confirmKeys,
        title: confirmKeys ? t(confirmKeys[0]) : '',
        content: confirmKeys ? t(confirmKeys[1]) : '',
        actions: [{ title: t('common.button.confirm'), callback: confirmPending, css: 'btn btn-block btn-primary' }],
    };

    return (
        // h-100 + flex column: 옆 요약 카드와 높이를 맞추고, 활성화/비활성화 버튼을 카드 맨 아래 왼쪽(mt-auto)에 —
        // 요약 카드의 [새로고침][Issue Voucher] 와 같은 선. 버튼 자리는 상태(활성/비활성)와 무관하게 같다.
        <div className="card border mb-0 h-100">
            <div className="card-body py-2 d-flex flex-column">
              <strong className="small">{t('pcActivation.title')}</strong>

                {/* 고객 정보 (= RAYTeams 계정 정보) — IMP-194: user_id · email 형식.
                    CustomerLicensesTable 의 표시 규약(userid → groupName fallback)과 일관.
                    BUG-113: N0003 (등록 없음) 시 seat / activations 카운트 배지는 미표시.
                    matchedVoucher.userid · email 자체가 RAYTeams 로그인 계정의 정보이므로
                    항상 표시 유지 — 어떤 팀즈 계정에 바우처 등록/사용 중인지 식별.
                    BUG-113 (사용자 요청 2026-05-13): rayteams 계정 정보를 카드 상단으로 이동.
                    다른 안내 메시지(자격 배지/로딩/스펙체크/피드백/타 voucher 점유)는 그 아래. */}
                <div className="small text-muted mt-1">
                    <span className="me-2">
                        <span className="fw-semibold me-1">{t('pcActivation.accountLabel')}:</span>
                        {accountName}
                        {showEmailSeparately && (
                            <span className="ms-1">· {accountEmail}</span>
                        )}
                    </span>
                    {isActivationUiVisible && (
                        <>
                            <span className="badge bg-secondary">
                                {interpolate(t('pcActivation.seatCount'), {
                                    active: activeCount,
                                    max: MAX_CONCURRENT_SEATS,
                                })}
                            </span>
                            {/* IMP-194: "Used PCs" → "Activations" — record.activationcount 가 누적 활성화
                                횟수(같은 PC 재활성화 시에도 +1) 의미로 변경된 것과 라벨 정합성. */}
                            <span className="badge bg-light text-dark ms-1">
                                {interpolate(t('pcActivation.activations'), {
                                    used: uniqueLimitCount,
                                })}
                            </span>
                        </>
                    )}
                </div>

                {/* 정보성 alert 공용화 (2026-05-14):
                    matchedVoucher 부재(virtual code='no-match') 와 LICENSE_CODE_BADGES
                    매핑 두 경로를 단일 alert 로 통합. 배경색/폰트 색 일관성 + 코드 중복 제거.
                    note: N0011 (seat 한도) + seatFull 분기 시 배지 + "비활성화 후 활성화"
                    버튼 안내가 동시 노출됨. 분기 우선순위 재설계는 별도 IMP. */}
                {/* 사용자 보고 (2026-05-14): matchedVoucher 부재 + 외부 dealer voucher
                    seat 활성 케이스에서는 occupiedByOther alert 가 SSoT 안내 (RAYTeams
                    기준 + group/type/dealer 표시). N0003 ("등록 필요") 메시지는 부정확
                    하고 중복이므로 hasOtherLicenseInstalled 가 true 일 때 숨김. */}
                {infoBadge && !hasOtherLicenseInstalled && (
                    <div
                        className={`alert alert-${infoBadge.variant} py-1 small mt-1 mb-0`}
                        data-testid="pc-activation-info-badge"
                        data-code={infoBadge.code}
                    >
                        {t(infoBadge.i18nKey)}
                    </div>
                )}

                {/* BUG-113 round 4 (PR #240 review Minor): localLicenseStatus 도착 전
                    "라이선스 상태 확인 중" 안내. 깜빡임 차단 후 빈 카드 혼란 차단. */}
                {isLicenseStatusPending && (
                    <div className="small text-muted mt-1" data-testid="pc-activation-license-checking">
                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                        {t('pcActivation.checking')}
                    </div>
                )}

                {/* IMP-175 + IMP-194 (2026-05-12): PC 권장 사양 체크 alert.
                    - 활성화 차단 X, 단순 안내. systemSpecCheck=null → silent.
                    - IMP-194 A: pcSpecChecker 가 측정 실패 항목 (cpuTier=0 / cpuCores=0 / ramGb=0 /
                      gpuName='') 을 failures 에서 제외 (silent). 정규식 매칭 실패로 인한 false
                      positive 차단.
                    - IMP-194 D: 사용자 dismiss 가능. sessionStorage 로 한 세션 동안 미표시.
                      IMP-207(옵션 A): dismiss 범위를 'specCheckDismissHint' 라벨 버튼으로 명시.
                    failures.length > 0 가드 추가 — A 적용으로 ok=false 이고 failures=[] 인 케이스는
                    이론상 없으나 방어적 차단. */}
                {systemSpecCheck && systemSpecCheck.ok === false
                    && Array.isArray(systemSpecCheck.failures) && systemSpecCheck.failures.length > 0
                    && !specCheckDismissed && (
                    <div className="alert alert-warning py-1 small mt-1 mb-0">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="fw-semibold">{t('pcActivation.specCheckTitle')}</span>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 small text-muted ms-2 text-nowrap"
                                onClick={dismissSpecCheck}
                                title={t('pcActivation.specCheckDismissHint')}
                                aria-label={t('pcActivation.specCheckDismissHint')}
                            >
                                {t('pcActivation.specCheckDismissHint')}
                            </button>
                        </div>
                        <div className="mb-1">{t('pcActivation.specCheckFailIntro')}</div>
                        <ul className="mb-0 ps-3">
                            {systemSpecCheck.failures.map((f) => (
                                <li key={f.key}>
                                    {interpolate(t(`pcActivation.specCheckFail.${f.key}`), {
                                        expected: f.expected,
                                        actual: f.actual,
                                    })}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* 피드백 메시지 — RTSQA-339 round 17 (2026-05-14): 15초 자동 dismiss +
                    수동 닫기 버튼. alert-dismissible + btn-close 패턴 (Bootstrap). */}
                {message && (
                    <div
                        className={`alert alert-${message.type} alert-dismissible py-1 small mt-1 mb-0`}
                        role="alert"
                    >
                        {message.text}
                        <button
                            type="button"
                            className="btn-close"
                            aria-label={t('common.button.close')}
                            onClick={dismissMessage}
                            style={{ fontSize: '0.65rem' }}
                        />
                    </div>
                )}

                {/* "다른 voucher 가 점유 중" 알림 — 동일 사용자의 다른 voucher (initial vs renewal 등).
                    사용자 요청 (2026-05-14):
                      - 닫기 버튼 (session dismiss, localLicenseStatus.sk 변경 시 reset).
                      - 기존 라이선스 식별 정보 (Group · Type) 두 번째 줄 표시. */}
                {hasOtherLicenseInstalled && !occupiedByOtherDismissed && (
                    <div
                        className="alert alert-info alert-dismissible py-1 small mt-1 mb-0"
                        role="alert"
                    >
                        {/* 사용자 요청 (2026-05-19): 외부 scope (matchedVoucher 부재) 케이스는
                            Activate/Deactivate 버튼 미노출이므로 "Activating will replace it"
                            문구가 부정확. isExternalScope 분기로 안내 문구 분리. */}
                        {isExternalScope
                            ? t('pcActivation.occupiedByExternalScope')
                            : t('pcActivation.occupiedByOther')}
                        {/* 사용자 요청 (2026-05-19): 식별 정보를 라벨별로 분리 표시 —
                            "KCK · Initial / RAY90095" 만 보면 의미 불명확.
                            RAYTeams Group / Type / Dealer ID 세 줄로 명확히. */}
                        {otherLicenseInfo?.name && (
                            <div className="mt-1">
                                <span className="fw-semibold">
                                    {t('pcActivation.groupLabel')}:
                                </span>
                                {' '}
                                {otherLicenseInfo.name}
                            </div>
                        )}
                        {otherLicenseInfo?.typeLabel && (
                            <div>
                                <span className="fw-semibold">{t('common.field.type')}:</span>
                                {' '}
                                {otherLicenseInfo.typeLabel}
                            </div>
                        )}
                        {otherLicenseInfo?.dealerLabel && (
                            <div>
                                <span className="fw-semibold">{t('license.label.dealer')}:</span>
                                {' '}
                                {otherLicenseInfo.dealerLabel}
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn-close"
                            aria-label={t('common.button.close')}
                            onClick={dismissOccupiedByOther}
                            style={{ fontSize: '0.65rem' }}
                        />
                    </div>
                )}

                {/* BUG-113: N0003 (등록 없음) 시 활성화 / 비활성화 버튼 미표시.
                    바우처 등록은 RAYGuard 책임 외. (활성화/비활성화 버튼은 위 머리 줄 오른쪽) */}

                {/* 상태 6: 비활성 + 좌석 2/2 → PC 목록 선택 후 [비활성화 후 활성화] */}
                {action === 'select-and-activate' && (
                    <div className="mt-1">
                        <p className="small mb-1">{t('pcActivation.selectPcToDeactivate')}</p>
                        <ul className="list-group list-group-flush small mb-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                            {activePcs.map((pc) => (
                                <li
                                    key={pc.rtid}
                                    className={`list-group-item list-group-item-action py-1 ${selectedPcRtid === pc.rtid ? 'active' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedPcRtid(pc.rtid === selectedPcRtid ? null : pc.rtid)}
                                >
                                    <span className="font-monospace">{pc.rtid}</span>
                                    {/* BUG-111 (2026-05-12, review Major #7): used[] 모델은 `created` 필드 사용
                                        — `activatedAt` 은 존재하지 않음. LicenseDetailModal 과 동일하게 변경. */}
                                    {pc.created && (
                                        <span className="ms-2 text-muted">
                                            {formatLicenseDate(pc.created)}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <button
                            className="btn btn-sm btn-warning"
                            disabled={!selectedPcRtid || loading || pcActivationLoading}
                            onClick={handleDeactivateOtherAndActivate}
                        >
                            {t('pcActivation.deactivateAndActivate')}
                        </button>
                    </div>
                )}

                {(action === 'deactivate' || action === 'activate') && (
                    <div className="d-flex flex-wrap gap-2 mt-auto pt-1">
                        {/* 상태 5: 이미 활성화됨 (이 voucher 의 활성화) → [비활성화] (IMP-223: resolver action 채택) */}
                        {action === 'deactivate' && (
                            <button
                                className="btn btn-sm btn-outline-danger"
                                style={{ margin: 0 }}
                                disabled={loading || pcActivationLoading}
                                onClick={handleDeactivate}
                            >
                                {t('pcActivation.deactivateBtn')}
                            </button>
                        )}

                        {/* 상태 4: 비활성 + 좌석 여유 → [활성화] */}
                        {action === 'activate' && (
                            <button
                                className="btn btn-sm btn-primary"
                                style={{ margin: 0 }}
                                disabled={loading || pcActivationLoading}
                                onClick={handleActivate}
                            >
                                {t('pcActivation.activateBtn')}
                            </button>
                        )}
                    </div>
                )}
            </div>
            <ModalConfirm info={confirmInfo} callbackClose={cancelPending} />
        </div>
    );
};

// IMP-169 (AC-2): connect 제거 — Redux state 는 컴포넌트 내부 useSelector 로 직접 구독.
export default PCActivationPanel;
